import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { format, startOfMonth, subDays } from 'date-fns';
import { CalendarIcon, TrendingUp, TrendingDown, Minus, LayoutGrid, MapPin } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';

const COLORS = ['#06B6D4','#3B82F6','#8B5CF6','#10B981','#F59E0B','#EF4444'];

const MetricRow = ({ label, values, format: fmt, highlight, best }) => (
  <div className={`grid gap-4 py-2.5 border-b border-slate-100 last:border-0 ${highlight ? 'bg-cyan-50/30 -mx-4 px-4 rounded' : ''}`}
    style={{ gridTemplateColumns: `180px repeat(${values.length}, 1fr)` }}>
    <span className={`text-xs font-medium ${highlight ? 'text-cyan-700' : 'text-slate-600'}`}>{label}</span>
    {values.map((v, i) => {
      const isBest = best !== undefined && v === best;
      return (
        <span key={`metric-${i}-${v}`} className={`text-xs font-semibold tabular-nums text-center ${
          isBest ? 'text-green-700' : highlight ? 'text-slate-800' : 'text-slate-700'
        }`}>
          {v == null ? '—' : fmt ? fmt(v) : v.toLocaleString()}
          {isBest && <span className="ml-1 text-green-500">▲</span>}
        </span>
      );
    })}
  </div>
);

const fmt$ = v => v != null ? '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }) : '—';
const fmtPct = v => v != null ? Number(v).toFixed(1) + '%' : '—';

export default function LocationComparisonPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [calFrom, setCalFrom] = useState(false);
  const [calTo, setCalTo] = useState(false);
  const [chartMetric, setChartMetric] = useState('fa_sold_total');
  // Column picker — up to 3 locations
  const [col1, setCol1] = useState('');
  const [col2, setCol2] = useState('');
  const [col3, setCol3] = useState('');
  const [showThird, setShowThird] = useState(false);

  const presets = [
    { label: 'This Month', from: format(startOfMonth(new Date()), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') },
    { label: 'Last 30d', from: format(subDays(new Date(), 30), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') },
    { label: 'Last 7d', from: format(subDays(new Date(), 7), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard/location-comparison`, { params: { date_from: dateFrom, date_to: dateTo } });
      setData(res.data);
      // Auto-select first two locations
      const locs = res.data.locations || [];
      if (locs.length >= 1 && !col1) setCol1(locs[0].location_id);
      if (locs.length >= 2 && !col2) setCol2(locs[1].location_id);
    } catch (err) { console.error('[LocationCompare]', err); toast.error('Failed to load comparison'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const allLocs = data?.locations || [];

  // Only show if 2+ locations
  if (!loading && allLocs.length < 2) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <LayoutGrid size={22} className="text-cyan-400" /> Location Comparison
          </h1>
        </div>
        <Card className="bg-white shadow-sm">
          <CardContent className="py-16 text-center">
            <MapPin className="mx-auto mb-3 text-slate-300" size={48} />
            <p className="text-slate-600 font-medium text-lg">You need at least 2 active locations</p>
            <p className="text-slate-500 text-sm mt-1">Add another location under Administration → Locations to use this view</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Build selected columns (filter out empty/unselected)
  const selectedIds = [col1, col2, showThird ? col3 : ''].filter(Boolean);
  const selectedLocs = selectedIds.map(id => allLocs.find(l => l.location_id === id)).filter(Boolean);
  const displayLocs = selectedLocs.length > 0 ? selectedLocs : allLocs.slice(0, 2);

  const CHART_METRICS = [
    { key: 'fa_sold_total', label: 'FA Sold' },
    { key: 'fa_presented', label: 'FA Presented' },
    { key: 'normalized_close_rate', label: 'Close Rate %' },
    { key: 'show_rate', label: 'Show Rate %' },
    { key: 'collections', label: 'Collections ($)' },
    { key: 'consults_showed', label: 'Consults Showed' },
  ];

  const chartData = displayLocs.map(l => ({
    name: l.location_name.length > 14 ? l.location_name.slice(0, 14) + '…' : l.location_name,
    value: l[chartMetric] || 0,
  }));

  const getMetricMax = (key) => displayLocs.length > 0 ? Math.max(...displayLocs.map(l => l[key] || 0)) : null;

  const metrics = [
    { label: 'Consults Scheduled', key: 'consults_scheduled' },
    { label: 'Consults Showed', key: 'consults_showed' },
    { label: 'Show Rate', key: 'show_rate', fmt: fmtPct, highlight: true },
    { label: 'FA Presented', key: 'fa_presented' },
    { label: 'FA Sold (Same Day)', key: 'fa_sold_same_day' },
    { label: 'FA Sold (Previous)', key: 'fa_sold_previous' },
    { label: 'FA Sold (Total)', key: 'fa_sold_total', highlight: true },
    { label: 'Arches Sold', key: 'arches_total' },
    { label: 'Same Day Close Rate', key: 'same_day_close_rate', fmt: fmtPct, highlight: true },
    { label: 'Normalized Close Rate', key: 'normalized_close_rate', fmt: fmtPct, highlight: true },
    { label: 'EOD Collections', key: 'collections', fmt: fmt$ },
    { label: 'Sales Revenue', key: 'revenue', fmt: fmt$ },
    { label: 'Inbound Calls', key: 'inbound_calls' },
    { label: 'Sales Count', key: 'sale_count' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <LayoutGrid size={22} className="text-cyan-400" /> Location Comparison
        </h1>
        <p className="text-slate-500 text-sm mt-1">Side-by-side KPI comparison across all active locations</p>
      </div>

      {/* Filters */}
      <Card className="bg-white shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            {presets.map(p => (
              <button key={p.label} onClick={() => { setDateFrom(p.from); setDateTo(p.to); }}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                  dateFrom === p.from && dateTo === p.to
                    ? 'border-cyan-400 text-cyan-600 bg-cyan-50'
                    : 'border-slate-200 text-slate-600 hover:border-cyan-300'
                }`}>
                {p.label}
              </button>
            ))}
            <Popover open={calFrom} onOpenChange={setCalFrom}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-8 text-xs gap-1.5 bg-white">
                  <CalendarIcon size={12} />
                  {dateFrom ? format(new Date(dateFrom + 'T00:00:00'), 'MMM d') : 'From'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white">
                <Calendar mode="single" selected={dateFrom ? new Date(dateFrom + 'T00:00:00') : undefined}
                  onSelect={d => { if (d) { setDateFrom(format(d, 'yyyy-MM-dd')); setCalFrom(false); } }} />
              </PopoverContent>
            </Popover>
            <Popover open={calTo} onOpenChange={setCalTo}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-8 text-xs gap-1.5 bg-white">
                  <CalendarIcon size={12} />
                  {dateTo ? format(new Date(dateTo + 'T00:00:00'), 'MMM d') : 'To'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white">
                <Calendar mode="single" selected={dateTo ? new Date(dateTo + 'T00:00:00') : undefined}
                  onSelect={d => { if (d) { setDateTo(format(d, 'yyyy-MM-dd')); setCalTo(false); } }} />
              </PopoverContent>
            </Popover>
          </div>
          {/* Column Picker */}
          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100 items-center">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Compare locations:</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Col 1 (primary)</span>
              <Select value={col1} onValueChange={setCol1}>
                <SelectTrigger className="h-7 text-xs w-44 bg-white"><SelectValue placeholder="Primary location" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {allLocs.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.location_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Col 2</span>
              <Select value={col2 || '__none__'} onValueChange={v => setCol2(v === '__none__' ? '' : v)}>
                <SelectTrigger className="h-7 text-xs w-44 bg-white"><SelectValue placeholder="Compare location" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="__none__">None</SelectItem>
                  {allLocs.filter(l => l.location_id !== col1).map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.location_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!showThird ? (
              allLocs.length > 2 && (
                <button onClick={() => setShowThird(true)}
                  className="text-xs text-cyan-400 hover:text-cyan-300 border border-cyan-400/30 rounded-md px-2 py-1">
                  + Add 3rd column
                </button>
              )
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">Col 3</span>
                <Select value={col3 || '__none__'} onValueChange={v => setCol3(v === '__none__' ? '' : v)}>
                  <SelectTrigger className="h-7 text-xs w-44 bg-white"><SelectValue placeholder="3rd location (optional)" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="__none__">None</SelectItem>
                    {allLocs.filter(l => l.location_id !== col1 && l.location_id !== col2).map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.location_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={() => { setShowThird(false); setCol3(''); }} className="text-xs text-slate-400 hover:text-red-400">✕</button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40 bg-white/5 rounded-xl" />)}
        </div>
      ) : allLocs.length === 0 ? (
        <Card className="bg-white shadow-sm">
          <CardContent className="py-16 text-center">
            <MapPin className="mx-auto mb-2 text-slate-300" size={40} />
            <p className="text-slate-500">No location data for this period</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Chart */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading">Location Comparison Chart</CardTitle>
                <Select value={chartMetric} onValueChange={setChartMetric}>
                  <SelectTrigger className="h-7 text-xs w-40 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {CHART_METRICS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip />
                  <Bar dataKey="value" name={CHART_METRICS.find(m => m.key === chartMetric)?.label} radius={[3,3,0,0]}>
                    {chartData.map((entry, i) => <Cell key={entry.name || i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* KPI Summary Cards */}
          <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${Math.min(displayLocs.length, 4)}, 1fr)` }}>
            {displayLocs.map((loc, i) => (
              <Card key={loc.location_id} className="bg-white shadow-sm"
                style={{ borderTop: `3px solid ${COLORS[i % COLORS.length]}` }}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold text-white"
                      style={{ background: COLORS[i % COLORS.length] }}>
                      {i + 1}
                    </div>
                    <p className="font-heading font-semibold text-slate-800 text-sm">{loc.location_name}</p>
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: 'FA Sold', value: loc.fa_sold_total, primary: true },
                      { label: 'Close Rate', value: fmtPct(loc.normalized_close_rate), primary: true },
                      { label: 'Show Rate', value: fmtPct(loc.show_rate) },
                      { label: 'FA Presented', value: loc.fa_presented },
                      { label: 'Collections', value: fmt$(loc.collections) },
                    ].map(m => (
                      <div key={m.label} className="flex justify-between items-center">
                        <span className="text-xs text-slate-500">{m.label}</span>
                        <span className={`text-xs font-semibold tabular-nums ${ m.primary ? 'text-slate-900' : 'text-slate-700' }`}>
                          {m.value ?? '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detailed Comparison Table */}
          {displayLocs.length > 0 && (
            <Card className="bg-white shadow-sm overflow-hidden">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-heading">Detailed Comparison Table</CardTitle>
                <p className="text-xs text-slate-400">▲ highlights the top location per metric</p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <div className="min-w-[600px] px-6 pb-4">
                    {/* Header row */}
                    <div className="grid gap-4 py-2 border-b-2 border-slate-200 mb-1"
                      style={{ gridTemplateColumns: `180px repeat(${displayLocs.length}, 1fr)` }}>
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Metric</span>
                      {displayLocs.map((loc, i) => (
                        <span key={loc.location_id} className="text-xs font-semibold text-center"
                          style={{ color: COLORS[i % COLORS.length] }}>
                          {loc.location_name}
                        </span>
                      ))}
                    </div>
                    {metrics.map(m => (
                      <MetricRow
                        key={m.key}
                        label={m.label}
                        values={displayLocs.map(l => l[m.key])}
                        format={m.fmt}
                        highlight={m.highlight}
                        best={getMetricMax(m.key)}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
